import type {
  GetSourceTransferAssignmentsInput,
  GetSupplierAssignmentsInput,
} from '@erp/rpc';
import type { BoardSharedFilters } from '../utils/boardSharedFilters';

/** What the board's line query for one cart is scoped to. */
export interface SupplierAssignmentsQuery {
  supplierId: string;
  divisionIds?: string[];
  warehouseIds?: string[];
  includeTrashed?: boolean;
  /** Keep lines released since the last refresh, marked released. */
  includeReleased?: boolean;
  shipToWarehouseId?: string;
  buyerCodes?: string[];
  lineFilters?: BoardSharedFilters;
}

/**
 * The board's line query input. The page reads the selected line from the
 * same cache entry as AssignmentBoard, so both must build it here.
 */
export function supplierAssignmentsInput({
  supplierId,
  divisionIds,
  warehouseIds,
  includeTrashed,
  includeReleased,
  shipToWarehouseId,
  buyerCodes,
  lineFilters,
}: SupplierAssignmentsQuery): GetSupplierAssignmentsInput {
  return {
    supplierId,
    divisionIds,
    warehouseIds,
    includeTrashed,
    includeReleased,
    shipToWarehouseId,
    buyerCodes,
    ...lineFilters,
  };
}

/** Who a board's carts are with: a vendor (POs) or a source location (TOs). */
export type CartOwner =
  | { kind: 'po'; supplierId: string }
  | { kind: 'to'; sourceWarehouseId: string };

export function cartOwnerId(owner: CartOwner): string {
  return owner.kind === 'po' ? owner.supplierId : owner.sourceWarehouseId;
}

/** The board's line query for one owner's carts. */
export type CartLinesQuery = Omit<SupplierAssignmentsQuery, 'supplierId'> & {
  owner: CartOwner;
};

/**
 * The transfer board's line query input (GEN-3026): the same scope and line
 * filters, for one source location. A cart is a destination.
 */
export function sourceTransferAssignmentsInput(
  query: CartLinesQuery,
  sourceWarehouseId: string,
): GetSourceTransferAssignmentsInput {
  return {
    sourceWarehouseId,
    divisionIds: query.divisionIds,
    warehouseIds: query.warehouseIds,
    includeTrashed: query.includeTrashed,
    includeReleased: query.includeReleased,
    destinationWarehouseId: query.shipToWarehouseId,
    buyerCodes: query.buyerCodes,
    ...query.lineFilters,
  };
}

/** The modal's prev/next source: the transfer board's, else the legacy bucket's. */
export function transferSiblingSourceId(source: {
  sourceId?: string;
  transferSourceId?: string;
}): string | undefined {
  return source.sourceId ?? source.transferSourceId;
}

/**
 * The detail modal's prev/next line query on a transfer board. A transfer
 * board source (`sourceId`) reads its board's exact line query, so the modal
 * reads the open cart's cached lines; the legacy vendorless bucket
 * (`transferSourceId`) keeps its own scope.
 */
export function transferSiblingsInput(
  query: Omit<CartLinesQuery, 'owner'>,
  source: { sourceId?: string; transferSourceId?: string },
): GetSourceTransferAssignmentsInput {
  if (source.sourceId != null) {
    return sourceTransferAssignmentsInput(
      { ...query, owner: { kind: 'to', sourceWarehouseId: source.sourceId } },
      source.sourceId,
    );
  }
  return {
    sourceWarehouseId: source.transferSourceId ?? '',
    divisionIds: query.divisionIds,
    warehouseIds: query.warehouseIds,
  };
}
